import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

export type Clarity = {
  /**
   * Sets a key-value pair for the current session.
   */
  (command: 'set', key: string, value: string | string[]): void;

  /**
   * Tracks a custom event.
   */
  (command: 'event', name: string, properties?: Record<string, unknown>): void;

  /**
   * Associates the current session with a specific user.
   */
  (
    command: 'identify',
    userId: string,
    sessionId?: string,
    pageId?: string,
    friendlyName?: string
  ): void;

  /**
   * Signals that the user has given consent for tracking.
   */
  (command: 'consent'): void;

  /**
   * Stops Clarity from collecting data.
   */
  (command: 'stop'): void;

  /**
   * Internal queue for commands before the script loads.
   */
  q?: Array<[string, string, unknown?]>;
};

declare global {
  interface Window {
    clarity?: Clarity;
  }
}

type ProviderProps = {
  projectId: string;
  defaultUserConsent?: boolean;
} & PropsWithChildren;

type ContextProps = {
  /**
   * Returns current users consent, if not explicitly set, defaults to defaultUserConsent.
   */
  consent: boolean;

  /**
   * Handles setting user consent for Microsoft Clarity tracking.
   * @param consent - Whether the user consents to tracking.
   */
  setConsent: (consent: boolean) => void;

  /**
   * Attaches user data to the current session for Microsoft Clarity.
   * @param userId - The user's unique identifier.
   * @param sessionId - Optional session identifier.
   */
  identifyUser: (userId: string, sessionId?: string) => void;

  /**
   * Logs a custom event to Microsoft Clarity.
   * @param eventName - The name of the event.
   * @param eventData - Optional event data.
   */
  logEvent: (eventName: string, eventData?: Record<string, unknown>) => void;
};

const MicrosoftClarityContext = createContext<ContextProps | undefined>(
  undefined
);

export const MicrosoftClarityProvider: FC<ProviderProps> = ({
  children,
  projectId,
  defaultUserConsent,
}) => {
  const [consent, setConsent] = useState<boolean>(defaultUserConsent ?? false);
  const [isUserIdentified, setIsUserIdentified] = useState<boolean>(false);
  const isProduction: boolean = process.env.NODE_ENV === 'production';

  useEffect(() => {
    // Only load the script in production
    if (!isProduction || !consent) return;

    // Check if script is already loaded
    if (window.clarity || document.getElementById('microsoft-clarity')) return;

    // Create and inject the Clarity script
    const script = document.createElement('script');
    script.id = 'microsoft-clarity';
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${projectId}");
    `;

    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return (): void => {
      const existingScript = document.getElementById('microsoft-clarity');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [projectId, isProduction, consent]);

  const handleSetConsent = (consent: boolean): void => {
    setConsent(consent);

    if (window.clarity) {
      if (consent) window.clarity('consent');
      else window.clarity('stop');
    }
  };

  const handleIdentifyUser = (userId: string, sessionId?: string): void => {
    if (!window.clarity || isUserIdentified) return;

    window.clarity('identify', userId, sessionId);
    setIsUserIdentified(true);
  };

  const logEvent = (
    eventName: string,
    eventData?: Record<string, unknown>
  ): void => {
    if (!window.clarity) {
      return;
    }

    window.clarity('event', eventName, eventData || {});
  };

  return (
    <MicrosoftClarityContext.Provider
      value={{
        consent,
        setConsent: handleSetConsent,
        identifyUser: handleIdentifyUser,
        logEvent,
      }}
    >
      {children}
    </MicrosoftClarityContext.Provider>
  );
};

/**
 * Custom hook to access Microsoft Clarity context.
 * @throws Error if used outside of MicrosoftClarityProvider.
 * @return Microsoft Clarity context.
 */

export const useMicrosoftClarity = (): ContextProps => {
  const context = useContext(MicrosoftClarityContext);

  if (!context) {
    throw new Error(
      'useMicrosoftClarity must be used within a MicrosoftClarityProvider'
    );
  }

  return context;
};
